import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        const savedUser = localStorage.getItem('user');
        return savedUser ? JSON.parse(savedUser) : null;
    });
    const [token, setToken] = useState(() => localStorage.getItem('access_token') || null);
    const [selectedYear, setSelectedYear] = useState(() => {
        const savedYear = localStorage.getItem('selected_year');
        return savedYear ? JSON.parse(savedYear) : null;
    });
    const [loading, setLoading] = useState(true);

    const selectYear = (year) => {
        setSelectedYear(year);
        if (year) {
            localStorage.setItem('selected_year', JSON.stringify(year));
        } else {
            localStorage.removeItem('selected_year');
        }
    };

    useEffect(() => {
        const verifyUser = async () => {
            if (token) {
                try {
                    // Try to fetch profile to verify token validity
                    const response = await api.get('/profile');
                    setUser(response.data);
                    localStorage.setItem('user', JSON.stringify(response.data));
                } catch (error) {
                    console.error("Token invalid or expired", error);
                    logout();
                }
            }
            setLoading(false);
        };

        verifyUser();
    }, [token]);

    const login = async (email, password) => {
        try {
            const response = await api.post('/login', { email, password });
            
            const { access_token, user: userData } = response.data;
            
            setToken(access_token);
            setUser(userData);
            
            localStorage.setItem('access_token', access_token);
            localStorage.setItem('user', JSON.stringify(userData));
            
            return { success: true };
        } catch (error) {
            console.error("Login Error", error);
            const message = error.response?.data?.message || 'Login failed. Please check your credentials.';
            return { success: false, message };
        }
    };

    const logout = async () => {
        try {
            if (token) {
                // Optionally call backend to invalidate token
                await api.post('/logout').catch(() => {});
            }
        } finally {
            setToken(null);
            setUser(null);
            localStorage.removeItem('access_token');
            localStorage.removeItem('user');
            localStorage.removeItem('selected_year');
            setSelectedYear(null);
        }
    };

    const hasPermission = (module, permission) => {
        if (!user) return false;
        if (user.role === 'Admin') return true;
        return user.permissions && user.permissions[module] && user.permissions[module][permission] === true;
    };

    return (
        <AuthContext.Provider value={{ user, token, selectedYear, selectYear, login, logout, loading, hasPermission }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};
